import { CodeOutlined } from "@ant-design/icons";
import { useSearch } from "@tanstack/react-router";
import { Button, Modal, Tooltip } from "antd";
import { useSelectedNode } from "components/CC/hooks/useDiagramActions";
import { NodeType } from "components/CC/types/enums.ts";
import { DrawerId } from "components/CC/types/models.ts";
import { NodeBlueprintDrawer } from "components/CC/UI/NodeBlueprintDrawer.tsx";
import { getCCDrawerSelectionParam } from "components/CC/utils/drawerUrlState.ts";
import { NodeFormData, serializeForDirtyCheck } from "components/CC/utils/nodeDefaults.ts";
import { Drawer } from "components/UI/Drawer";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent.tsx";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle.tsx";
import { useDrawer } from "hooks/useDrawer.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { cloneElement, isValidElement, ReactElement, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { usePendingDrawerVisibility } from "utils/drawerVisibilityStore";

// Map DrawerId to NodeType/EdgeType for Blueprint drawer
const DRAWER_TO_NODE_TYPE: Partial<Record<DrawerId, string>> = {
    [DrawerId.Set]: NodeType.Set,
    [DrawerId.ModifyField]: NodeType.Modify,
    [DrawerId.Http]: NodeType.Http,
    [DrawerId.HttpMethod]: NodeType.Methods,
    [DrawerId.Webhook]: NodeType.Webhook,
    [DrawerId.Start]: NodeType.Start,
    [DrawerId.End]: NodeType.End,
    [DrawerId.Wait]: NodeType.Wait,
    [DrawerId.Repeater]: NodeType.Repeater,
    [DrawerId.SubScenario]: NodeType.SubScenario,
    [DrawerId.Mirror]: NodeType.Mirror,
    [DrawerId.Condition]: "conditionEdge",
};

interface BaseConnectionCreatorDrawerProps {
    drawerId: DrawerId;
    title: string;
    children: ReactNode;
    /** Custom drawer width (default: 854) */
    width?: number | string;
}

export const BaseConnectionCreatorDrawer = ({ drawerId, title, children, width }: BaseConnectionCreatorDrawerProps) => {
    const drawerUtils = useDrawer();
    const { selectedNode, deleteSelectedNode, updateNodeFormData } = useSelectedNode();
    const selectionParam = getCCDrawerSelectionParam(drawerId);
    const searchParams = useSearch({ strict: false }) as Record<string, unknown>;
    const hasUrlRestorationPending =
        typeof searchParams[selectionParam] === "string" && (searchParams[selectionParam] as string).length > 0;

    // Store latest form data so we can save on close
    const formDataRef = useRef<NodeFormData | null>(null);
    // Store initial form data to compare against (set on first onChange call)
    const initialFormDataRef = useRef<string | null>(null);
    // Track if changes were saved via Done button
    const changesSavedRef = useRef(false);

    // Blueprint drawer state
    const [blueprintDrawerOpen, setBlueprintDrawerOpen] = useState(false);
    // Key to force form remount after blueprint apply
    const [formResetKey, setFormResetKey] = useState(0);
    // Optional validator registered by form — returns true if valid (shows errors itself)
    const validateFormRef = useRef<(() => Promise<boolean>) | null>(null);

    // Reset refs for next drawer open
    const resetRefs = useCallback(() => {
        formDataRef.current = null;
        initialFormDataRef.current = null;
        changesSavedRef.current = false;
        validateFormRef.current = null;
    }, []);

    const lastSelectedNodeIdRef = useRef<string | null>(null);

    // Close drawer (including from URL) if it was restored from URL but no node is selected.
    // hadSelectionWhileOpenRef guards against closing when selectedNode becomes briefly falsy
    // after a real interaction — only close when the drawer opened with no selection at all.
    // Reset refs on close so that onChange calls during the Ant Design close animation do not
    // re-populate initialFormDataRef and cause a false "unsaved changes" warning next open.
    const pendingVisibility = usePendingDrawerVisibility(drawerId.toString());
    const isOpen = pendingVisibility ?? drawerUtils.isDrawerActive(drawerId);
    const hadSelectionWhileOpenRef = useRef(false);
    useEffect(() => {
        if (!isOpen) {
            hadSelectionWhileOpenRef.current = false;
            lastSelectedNodeIdRef.current = null;
            resetRefs();
            return;
        }

        if (selectedNode) {
            if (lastSelectedNodeIdRef.current !== selectedNode.id) {
                lastSelectedNodeIdRef.current = selectedNode.id;
                // Node changed (URL restoration or switching between nodes).
                // Reset so the fresh form remount captures the correct initial state.
                resetRefs();
            }
            hadSelectionWhileOpenRef.current = true;
            return;
        }

        // If the URL has a selectionParam, the diagram's restoration useEffect will either
        // restore the node or close the drawer — don't race it by closing prematurely.
        if (!hadSelectionWhileOpenRef.current && !hasUrlRestorationPending) {
            drawerUtils.hide(drawerId, [selectionParam]);
        }
    }, [isOpen, selectedNode, drawerUtils, drawerId, resetRefs, selectionParam, hasUrlRestorationPending]);
    const { t: tC } = usePaldockTranslation("common");
    const { t } = usePaldockTranslation("components.connection-creator");

    const handleDelete = () => {
        deleteSelectedNode();
        resetRefs();
        drawerUtils.hide(drawerId, [selectionParam]);
    };

    // Called on every form change to keep track of current values
    const handleFormChange = useCallback((data: NodeFormData) => {
        // Store the first value as initial (this captures form defaults)
        if (initialFormDataRef.current === null) {
            initialFormDataRef.current = serializeForDirtyCheck(data);
        }
        formDataRef.current = data;
    }, []);

    // Save form data to node state and close drawer
    const handleDone = async () => {
        // If form registered a validator, run it first (it shows its own errors)
        if (validateFormRef.current) {
            const isValid = await validateFormRef.current();
            if (!isValid) return;
        }
        if (formDataRef.current) {
            updateNodeFormData(formDataRef.current);
        }
        resetRefs();
        drawerUtils.hide(drawerId, [selectionParam]);
    };

    // Check if form has unsaved changes
    const hasUnsavedChanges = useCallback(() => {
        if (changesSavedRef.current) {
            return false;
        }

        const currentData = formDataRef.current;

        if (!currentData || initialFormDataRef.current === null) {
            return false;
        }

        return serializeForDirtyCheck(currentData) !== initialFormDataRef.current;
    }, []);

    // Handle blueprint apply - updates node directly and forces form remount
    const handleBlueprintApply = useCallback(
        (newFormData: NodeFormData) => {
            // Update the node form data directly
            updateNodeFormData(newFormData);
            // Update local ref so drawer state stays in sync
            formDataRef.current = newFormData;
            initialFormDataRef.current = serializeForDirtyCheck(newFormData);
            // Force form to remount with new values
            setFormResetKey((k) => k + 1);
        },
        [updateNodeFormData]
    );

    // Handle close with unsaved changes warning
    const handleBeforeClose = useCallback((): Promise<boolean> => {
        // Reset the saved flag for next time
        if (changesSavedRef.current) {
            resetRefs();
            return Promise.resolve(true);
        }

        if (!hasUnsavedChanges()) {
            resetRefs();
            return Promise.resolve(true);
        }

        return new Promise((resolve) => {
            Modal.confirm({
                title: tC("unsaved_changes"),
                content: tC("unsaved_changes_message"),
                okText: tC("discard"),
                cancelText: tC("cancel"),
                onOk: () => {
                    resetRefs();
                    resolve(true);
                },
                onCancel: () => resolve(false),
            });
        });
    }, [hasUnsavedChanges, tC, resetRefs]);

    // Inject onChange and onRegisterValidator handlers into the child form component
    const childrenWithHandlers = isValidElement(children)
        ? cloneElement(
              children as ReactElement<{
                  onChange?: (data: NodeFormData) => void;
                  onRegisterValidator?: (fn: () => Promise<boolean>) => void;
              }>,
              {
                  onChange: handleFormChange,
                  onRegisterValidator: (fn: () => Promise<boolean>) => {
                      validateFormRef.current = fn;
                  },
              }
          )
        : children;

    return (
        <>
            <Drawer
                drawerId={drawerId}
                title={<DrawerTitle mainTitle={title} />}
                onBeforeClose={handleBeforeClose}
                searchParamsToClean={[selectionParam]}
                width={width}
                extraButtons={
                    <>
                        {/* Only show blueprint button for drawers with type mapping */}
                        {DRAWER_TO_NODE_TYPE[drawerId] && (
                            <Tooltip title={t("blueprint.title")}>
                                <Button
                                    size={"large"}
                                    type={"default"}
                                    icon={<CodeOutlined />}
                                    onClick={() => setBlueprintDrawerOpen(true)}
                                    aria-label={t("blueprint.title")}
                                />
                            </Tooltip>
                        )}
                        <Button size={"large"} type={"primary"} danger onClick={handleDelete}>
                            {tC("delete")}
                        </Button>
                        <Button size={"large"} type={"primary"} onClick={handleDone}>
                            {tC("save")}
                        </Button>
                    </>
                }
            >
                <DrawerContent key={formResetKey}>{childrenWithHandlers}</DrawerContent>
            </Drawer>

            {/* Node Blueprint Drawer */}
            <NodeBlueprintDrawer
                open={blueprintDrawerOpen}
                onClose={() => setBlueprintDrawerOpen(false)}
                nodeType={DRAWER_TO_NODE_TYPE[drawerId] || ""}
                formData={formDataRef.current || selectedNode?.formData}
                onApply={handleBlueprintApply}
            />
        </>
    );
};
